// askSmart-ui/src/pages/TOSP/components/OccupancyCard.tsx
import React from "react";
import { formatCurrency, formatDate } from "../format";
import {
  LeasedSpaceDraft,
  OccupancyDraft,
  draftEscalation,
  draftSchedule,
  draftText,
  emptyLeasedSpaceDraft,
} from "../occupancyDraft";
import { useTospData } from "../TospDataContext";
import {
  chargesPercentageRent,
  dateUnderContract,
  isOccupancyActive,
  NaturalBreakpointYear,
  naturalBreakpointYear,
  nextRenewalDate,
  Unit,
} from "../types";
import { parseNumeric } from "../validation";
import DeleteConfirmModal from "../../../components/DeleteConfirmModal";
import LeasedSpaceCard from "./LeasedSpaceCard";
import NaturalBreakpointDetail from "./NaturalBreakpointDetail";

// Defined on the space card, which is what actually offers the choice, and
// re-exported here because callers reach for it alongside this component.
// Declaring it here instead would make the two files import each other.
export type { UnitOption } from "./LeasedSpaceCard";

interface OccupancyCardProps {
  draft: OccupancyDraft;
  expanded: boolean;
  onToggle: () => void;
  onChange: (patch: Partial<OccupancyDraft>) => void;
  onRemove: () => void;
  /** Called with the space id — a unit choice may start a takeover. */
  onChangeSpace: (spaceId: string, patch: Partial<LeasedSpaceDraft>) => void;
  unitOptionsFor: (space: LeasedSpaceDraft) => UnitOption[];
  unitById: (unitId: string) => Unit | undefined;
  /** Validation messages for this lease, keyed by field or "space_<id>_<field>". */
  errors: Record<string, string>;
  /** Viewing rather than editing: same layout, nothing changeable. */
  readOnly?: boolean;
  /**
   * Shows the Lease End Date box.
   *
   * Off everywhere but a past tenancy. A live lease ends by its term running out
   * or by a takeover writing the date, and offering the field on every card would
   * invite somebody to end a lease by typing in it.
   */
  allowLeaseEnd?: boolean;
  /** Opens the lease review workspace for this lease. */
  onReviewLease?: () => void;
  /**
   * What the header reads, when the card IS the section.
   *
   * With one lease per tenant the section heading and the card heading were
   * the same thing stacked twice, so the card takes the heading and the
   * expander sits beside it.
   */
  sectionTitle?: string;
}

// One lease, edited in place on the Tenant form as a collapsible card.
//
// The card models the agreement, not a physical space. Everything the lease
// states once — its dates, its term, its extensions, the two percentage rent
// rates, the deposit and the clauses — is on this card; the areas it demises
// are the Leased Spaces below, each carrying its own rent.
export default function OccupancyCard({
  draft,
  expanded,
  onToggle,
  onChange,
  onRemove,
  onChangeSpace,
  unitOptionsFor,
  unitById,
  errors,
  readOnly = false,
  allowLeaseEnd = false,
  onReviewLease,
  sectionTitle,
}: OccupancyCardProps) {
  // BMVA's two portfolio-wide figures — the rate and the royalty — are set on
  // the Configuration page, not negotiated here.
  const { configuration } = useTospData();
  const [openSpaces, setOpenSpaces] = React.useState<string[]>([]);
  const [breakdownOpen, setBreakdownOpen] = React.useState(false);
  // The areas are what an admin opens an occupancy to look at — the units,
  // the rents, what's assigned. The lease's own terms are settled once at
  // review and rarely touched again, so they start folded away.
  const [leaseOpen, setLeaseOpen] = React.useState(false);
  const [areasOpen, setAreasOpen] = React.useState(true);
  // Removing an area or the whole lease is a click away from losing the unit
  // it was assigned to and any renewals recorded against it. Nothing here is
  // written until the tenant is saved, but there is no undo inside the form,
  // so both are asked about — the same way every other delete in TOSP is.
  const [removingSpace, setRemovingSpace] = React.useState<string | null>(null);
  const [removingLease, setRemovingLease] = React.useState(false);
  /** Sales Reported has been switched to No and is awaiting confirmation. */
  const [confirmNoSales, setConfirmNoSales] = React.useState(false);

  const set = (key: string, value: string) =>
    onChange({ [key]: value } as Partial<OccupancyDraft>);
  const text = (key: string) => draftText(draft, key);

  // Turning the toggle on opens a first, empty term to type into.
  const toggleExtensionTerm = (enabled: boolean) =>
    onChange({
      extensionTermEnabled: enabled,
      extensionTerms: enabled && draft.extensionTerms.length === 0 ? [""] : draft.extensionTerms,
    });

  const setTerm = (index: number, value: string) =>
    onChange({
      extensionTerms: draft.extensionTerms.map((t, i) => (i === index ? value : t)),
    });

  const spaces = draft.leasedSpaces;
  const hasErrors = Object.keys(errors).length > 0;
  const schedule = draftSchedule(draft);

  const breakpointRate = parseNumeric(text("percentageRentThresholdRate"));

  // The lease's breakpoint: each qualifying space's own, added up, keeping the
  // per-space workings so the "i" has something to show. Built from the drafts
  // rather than the saved record so it tracks the form live.
  const leaseBreakpoint = React.useMemo(() => {
    const lines: { id: string; label: string; breakdown: NaturalBreakpointYear }[] = [];
    let total: number | null = null;

    spaces.forEach((space, index) => {
      const unit = space.unitId ? unitById(space.unitId) : undefined;
      if (!chargesPercentageRent(configuration, unit)) return;
      const breakdown = naturalBreakpointYear(
        draftEscalation(draft, space),
        schedule,
        unit?.occupancySqFt,
        breakpointRate,
      );
      if (breakdown.total === null) return;
      total = (total ?? 0) + breakdown.total;
      lines.push({
        id: space.id,
        label: space.name.trim() || `Leased Space ${index + 1}`,
        breakdown,
      });
    });

    return { lines, total };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spaces, breakpointRate, configuration, draft]);

  const toggleSpace = (id: string) =>
    setOpenSpaces((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const addSpace = () => {
    const space = emptyLeasedSpaceDraft();
    onChange({ leasedSpaces: [...spaces, space] });
    setOpenSpaces((prev) => [...prev, space.id]);
  };

  // Any space holding a validation message opens itself, so the reason a save
  // was refused can't sit behind a collapsed header.
  React.useEffect(() => {
    const failed = spaces
      .filter((space) => Object.keys(errors).some((key) => key.startsWith(`space_${space.id}_`)))
      .map((space) => space.id);
    if (failed.length) {
      setOpenSpaces((prev) => [...new Set([...prev, ...failed])]);
      setAreasOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const errorsForSpace = (id: string): Record<string, string> => {
    const prefix = `space_${id}_`;
    return Object.entries(errors)
      .filter(([key]) => key.startsWith(prefix))
      .reduce((acc, [key, value]) => ({ ...acc, [key.slice(prefix.length)]: value }), {});
  };

  const dateField = (key: string, label: string) => (
    <div className="tosp-field">
      <label className="tosp-label" htmlFor={`tosp-occ-${draft.id}-${key}`}>
        {label}
      </label>
      <input
        id={`tosp-occ-${draft.id}-${key}`}
        type="date"
        className="manage-input"
        disabled={readOnly}
        value={text(key)}
        onChange={(e) => set(key, e.target.value)}
      />
      {errors[key] && <p className="tosp-field-error">{errors[key]}</p>}
    </div>
  );

  // A numeric entry carrying its unit: "$" ahead of the figure, "%" after it.
  const numberField = (key: string, label: string, unit: "$" | "%" | null = null) => (
    <div className="tosp-field">
      <label className="tosp-label" htmlFor={`tosp-occ-${draft.id}-${key}`}>
        {label}
      </label>
      <div className="tosp-affix-wrap">
        {unit === "$" && <span className="tosp-affix tosp-affix-prefix">$</span>}
        <input
          id={`tosp-occ-${draft.id}-${key}`}
          className={`manage-input${
            unit === "$" ? " tosp-has-prefix" : unit === "%" ? " tosp-has-suffix" : ""
          }`}
          autoComplete="off"
          inputMode="decimal"
          disabled={readOnly}
          value={text(key)}
          onChange={(e) => set(key, e.target.value)}
        />
        {unit === "%" && <span className="tosp-affix tosp-affix-suffix">%</span>}
      </div>
      {errors[key] && <p className="tosp-field-error">{errors[key]}</p>}
    </div>
  );

  // A free-text term, e.g. "120 months". The leading figure is what the
  // schedule maths reads.
  const termField = (key: string, label: string) => (
    <div className="tosp-field">
      <label className="tosp-label" htmlFor={`tosp-occ-${draft.id}-${key}`}>
        {label}
      </label>
      <input
        id={`tosp-occ-${draft.id}-${key}`}
        className="manage-input"
        autoComplete="off"
        placeholder="e.g. 60 months"
        disabled={readOnly}
        value={text(key)}
        onChange={(e) => set(key, e.target.value)}
      />
    </div>
  );

  const calcDateField = (
    label: string,
    value: string | null,
    hint: string,
    highlight = false,
  ) => (
    <div className="tosp-field">
      <span className="tosp-label">{label}</span>
      <div
        className={`tosp-calc-value${highlight ? " tosp-calc-value-live" : ""}`}
        title={hint}
      >
        {value ? formatDate(value) : "—"}
      </div>
    </div>
  );

  // `blue` paints the box in the amendment colour — used for fields an LAA
  // changed, so what came from an amendment reads apart from the original lease.
  const textAreaField = (key: string, label: string, blue = false) => (
    <div className="tosp-field" style={{ marginBottom: 16 }}>
      <label className="tosp-label" htmlFor={`tosp-occ-${draft.id}-${key}`}>
        {label}
      </label>
      <textarea
        id={`tosp-occ-${draft.id}-${key}`}
        className={`manage-input${blue ? " tosp-amended" : ""}`}
        rows={2}
        disabled={readOnly}
        value={text(key)}
        onChange={(e) => set(key, e.target.value)}
      />
    </div>
  );

  return (
    <div className={`tosp-accordion${hasErrors ? " tosp-accordion-error" : ""}`}>
      {/* The line reads as what the record is and how much of it there is.
          Everything you can DO with the lease belongs to one half of it or the
          other, so the buttons live on the sub-sections rather than up here
          where they'd apply to nothing in particular. */}
      <div className="tosp-accordion-header">
        <button
          type="button"
          className="tosp-accordion-toggle"
          onClick={onToggle}
          aria-expanded={expanded}
          title={expanded ? "Collapse" : "Expand"}
        >
          <span className="tosp-accordion-caret">{expanded ? "▾" : "▸"}</span>
          <span className="tosp-accordion-title">{sectionTitle || "Occupancy"}</span>
          <span className="tosp-occupancy-status">
            {spaces.length} Area{spaces.length === 1 ? "" : "s"} ·{" "}
            {isOccupancyActive(schedule) ? "Active" : "Ended"}
          </span>
        </button>
      </div>

      {expanded && (
        <div className="tosp-accordion-body">
          {/* The lease's own terms. The way into the document sits here,
              because this is the half of the record it fills in. */}
          <div className="tosp-subsection">
            <div className="tosp-subsection-header">
              <button
                type="button"
                className="tosp-subsection-toggle"
                aria-expanded={leaseOpen}
                onClick={() => setLeaseOpen((open) => !open)}
              >
                <span className="tosp-accordion-caret">{leaseOpen ? "▾" : "▸"}</span>
                <span className="tosp-subsection-title">Lease Information</span>
              </button>
              {!readOnly && onReviewLease && (
                <button
                  type="button"
                  className="tosp-btn-secondary tosp-btn-hover-blue"
                  onClick={onReviewLease}
                  title="Review the signed lease and fill these fields from it"
                >
                  Review Lease
                </button>
              )}
              {!readOnly && (
                <button
                  type="button"
                  className="tosp-btn-secondary tosp-btn-danger"
                  onClick={() => setRemovingLease(true)}
                  title="Remove this lease"
                >
                  Remove
                </button>
              )}
            </div>

            <div hidden={!leaseOpen}>

          {/* Who the lease is with, on a line of its own and labelled beside
              the box rather than above it — it heads the terms, it isn't one
              of them. */}
          <div className="tosp-field tosp-field-inline">
            <label
              className="tosp-label"
              htmlFor={`tosp-occ-${draft.id}-landlordLegalEntity`}
            >
              Landlord Legal Entity
            </label>
            <input
              id={`tosp-occ-${draft.id}-landlordLegalEntity`}
              className="manage-input"
              autoComplete="off"
              disabled={readOnly}
              value={text("landlordLegalEntity")}
              onChange={(e) => set("landlordLegalEntity", e.target.value)}
            />
          </div>

          <div className="tosp-form-row-4">
            {dateField("occupancyDate", "Occupancy Date")}
            {dateField("openingDate", "Opening Date")}
            {dateField("leaseCommenceDate", "Lease Commencement Date")}
            {dateField("leaseExecutionDate", "Lease Execution Date")}
          </div>

          {/* The term and the two dates it produces, on one line — the figure
              entered beside what it works out to. */}
          <div className="tosp-form-row-4">
            {termField("leaseTerm", "Lease Term")}
            {calcDateField(
              "Next Renewal Date",
              nextRenewalDate(schedule),
              "The end of the term currently running — calculated",
              true,
            )}
            {calcDateField(
              "Date Under Contract",
              dateUnderContract(schedule),
              "The end of the last extension — calculated",
            )}
            {/* An explicit end, which overrides the two calculated dates beside
                it. Normally written by a takeover or by moving a tenant to Past
                Tenants rather than typed — a lease that simply runs its course
                has none.

                Once it HAS one the tenancy is over, so the date is stated rather
                than offered for editing: a red pill, which is the module's
                colour for a lease that has ended and the same one the Lease
                column uses. Editable only where somebody is still entering it —
                a past tenancy being recorded after the fact. */}
            {draft.leaseEndDate && !allowLeaseEnd ? (
              <div className="tosp-field">
                <span className="tosp-label">Exit Date</span>
                <div>
                  <span
                    className="tosp-lease-flag tosp-lease-expired"
                    title="The last day of the lease — this tenancy has ended"
                  >
                    {formatDate(draftText(draft, "leaseEndDate"))}
                  </span>
                </div>
              </div>
            ) : (
              allowLeaseEnd && dateField("leaseEndDate", "Lease End Date")
            )}
          </div>

          {/* Its own line, so the toggle and the term boxes it reveals have
              the full width of the card to sit on together. */}
          <div className="tosp-field tosp-extension-field">
            <span className="tosp-label">Extension Terms</span>
            <div className="tosp-extension-terms">
              <label className="switch">
                <input
                  type="checkbox"
                  checked={draft.extensionTermEnabled}
                  disabled={readOnly}
                  onChange={(e) => toggleExtensionTerm(e.target.checked)}
                />
                <span className="slider" />
              </label>

              {draft.extensionTermEnabled && (
                <>
                  {draft.extensionTerms.map((term, index) => {
                    // Used terms are counted from the first, so an extension is
                    // used exactly when its position is below the used count.
                    const used = index < (draft.extensionTermsUsed ?? 0);
                    return (
                      // Index-keyed on purpose: the terms are an ordered list of
                      // free text with no identity of their own.
                      <div
                        key={index}
                        className={`tosp-extension-term${
                          used ? " tosp-extension-term-used" : ""
                        }`}
                      >
                        <input
                          className="manage-input"
                          autoComplete="off"
                          aria-label={`Extension term ${index + 1}`}
                          placeholder="e.g. 60 months"
                          disabled={readOnly}
                          value={term}
                          onChange={(e) => setTerm(index, e.target.value)}
                          // The box itself carries the used state — blue, and
                          // saying so on hover. Nothing sits beside it: a used
                          // extension has been exercised, so there is no
                          // freeing it and no deleting it. A term granted
                          // later is a new one, added with +.
                          title={used ? "Extension Used" : undefined}
                        />
                        {!readOnly && !used && (
                          <>
                            {/* Marks this extension used. Kept contiguous from
                                the first: marking term i used marks every
                                earlier one too. */}
                            <button
                              type="button"
                              className="tosp-btn-icon tosp-btn-icon-sm tosp-extension-used-toggle"
                              title="Mark this extension as used"
                              onClick={() => onChange({ extensionTermsUsed: index + 1 })}
                            >
                              ○
                            </button>
                            <button
                              type="button"
                              className="tosp-btn-icon tosp-btn-delete tosp-btn-icon-sm"
                              title="Remove this term"
                              onClick={() =>
                                onChange({
                                  extensionTerms: draft.extensionTerms.filter(
                                    (_, i) => i !== index,
                                  ),
                                })
                              }
                            >
                              ×
                            </button>
                          </>
                        )}
                      </div>
                    );
                  })}
                  {!readOnly && (
                    <button
                      type="button"
                      className="tosp-btn-secondary tosp-btn-add-term"
                      title="Add another extension term"
                      onClick={() => onChange({ extensionTerms: [...draft.extensionTerms, ""] })}
                    >
                      +
                    </button>
                  )}
                </>
              )}
            </div>
          </div>

          {/* Two rates are entered and kept apart on purpose: the breakpoint
              rate sets the sales figure percentage rent starts at, the
              percentage rent rate is what's charged above it. Today's leases
              use the same figure for both, but nothing here assumes that.
              Both are the agreement's, and reach only the spaces whose Space
              Type attracts percentage rent. */}
          <div className="tosp-form-row-4">
            {numberField("percentageRentThresholdRate", "Breakpoint Rate", "%")}
            {numberField("percentageRentPayment", "Percentage Rent Rate", "%")}

            <div className="tosp-field">
              <span className="tosp-label">
                Natural Breakpoint
                <button
                  type="button"
                  className="tosp-info-btn"
                  aria-expanded={breakdownOpen}
                  title={breakdownOpen ? "Hide the calculation" : "How this is calculated"}
                  onClick={() => setBreakdownOpen((open) => !open)}
                >
                  i
                </button>
              </span>
              <div
                className="tosp-calc-value tosp-calc-value-live"
                title="Every leased space that attracts percentage rent, added up"
              >
                {leaseBreakpoint.total === null
                  ? "—"
                  : formatCurrency(leaseBreakpoint.total)}
              </div>
            </div>

            {/* Not every tenant files sales — a government retailer or a chain
                on a flat rent never will. Left reporting, they sit in the
                overdue list every month and bury the tenants genuinely behind.
                Absent means Yes, so every lease written before this reports. */}
            <div className="tosp-field">
              <span className="tosp-label">Sales Reported</span>
              <div className="tosp-radio-group" role="radiogroup" aria-label="Sales Reported">
                <label className="tosp-radio">
                  <input
                    type="radio"
                    name={`tosp-sales-reported-${draft.id}`}
                    checked={draft.salesReported !== false}
                    disabled={readOnly}
                    onChange={() => onChange({ salesReported: true })}
                  />
                  <span>Yes</span>
                </label>
                <label className="tosp-radio">
                  <input
                    type="radio"
                    name={`tosp-sales-reported-${draft.id}`}
                    checked={draft.salesReported === false}
                    disabled={readOnly}
                    // Confirmed before it takes, not after: switching this off
                    // stops asking the tenant for figures the lease may still
                    // entitle the landlord to.
                    onChange={() => setConfirmNoSales(true)}
                  />
                  <span>No</span>
                </label>
              </div>
            </div>
          </div>

          {/* One block per space that carries a breakpoint, then the lease's
              own figure. A tenant is measured against the total, so the total
              is what has to be shown to be made of something. */}
          {breakdownOpen && (
            <div className="tosp-breakpoint-stack">
              {leaseBreakpoint.lines.length === 0 ? (
                <p className="tosp-field-hint">
                  No leased space attracts percentage rent yet — assign a unit whose
                  Space Type does, and set a Breakpoint Rate.
                </p>
              ) : (
                <>
                  {leaseBreakpoint.lines.map((line) => (
                    <NaturalBreakpointDetail
                      key={line.id}
                      breakdown={line.breakdown}
                      heading={`${line.label} — ${line.breakdown.year} Natural Breakpoint`}
                      hideTotal={leaseBreakpoint.lines.length > 1}
                    />
                  ))}
                  {leaseBreakpoint.lines.length > 1 && leaseBreakpoint.total !== null && (
                    <div className="tosp-breakpoint-total">
                      <span>Lease total</span>
                      <strong>{formatCurrency(leaseBreakpoint.total)}</strong>
                    </div>
                  )}
                </>
              )}
            </div>
          )}

          {textAreaField("rentRenewalNotations", "Rent Renewal Notes")}
          {textAreaField("useClauseSummary", "Use Clause Summary")}
          {textAreaField("exclusiveSummary", "Exclusive Summary")}
          {/* Last, and after the lease's own summaries: this records what an
              amendment changed beyond the extracted figures, so it reads as an
              addition to the terms above rather than one of them. Always in the
              amendment blue, since the box exists for amendment changes. */}
          {textAreaField("extensionNotes", "Extension Notes", true)}

            </div>
          </div>

          {/* Each area the lease demises, with its own rent. Built by the
              lease review, which detects them in the document and asks the
              admin which of them need a TOSP unit — so adding one by hand
              belongs here, beside them. */}
          <div className="tosp-subsection">
            <div className="tosp-subsection-header">
              <button
                type="button"
                className="tosp-subsection-toggle"
                aria-expanded={areasOpen}
                onClick={() => setAreasOpen((open) => !open)}
              >
                <span className="tosp-accordion-caret">{areasOpen ? "▾" : "▸"}</span>
                <span className="tosp-subsection-title">Leased Areas</span>
              </button>
              {!readOnly && (
                <button
                  type="button"
                  className="tosp-btn-secondary tosp-btn-hover-blue"
                  onClick={addSpace}
                >
                  + Add Leased Area
                </button>
              )}
            </div>

            <div hidden={!areasOpen}>

            {spaces.length === 0 ? (
              <p className="tosp-field-hint">
                {readOnly
                  ? "No leased areas recorded."
                  : "No leased areas yet. Review the lease to detect them, or add one by hand."}
              </p>
            ) : (
              spaces.map((space, index) => (
                <LeasedSpaceCard
                  key={space.id}
                  occupancy={draft}
                  draft={space}
                  position={index + 1}
                  expanded={openSpaces.includes(space.id)}
                  onToggle={() => toggleSpace(space.id)}
                  onChange={(patch) => onChangeSpace(space.id, patch)}
                  onRemove={() => setRemovingSpace(space.id)}
                  unitOptions={unitOptionsFor(space)}
                  unitById={unitById}
                  errors={errorsForSpace(space.id)}
                  readOnly={readOnly}
                />
              ))
            )}

            {errors.leasedSpaces && (
              <p className="tosp-field-error">{errors.leasedSpaces}</p>
            )}
            </div>
          </div>
        </div>
      )}

      <DeleteConfirmModal
        isOpen={!!removingSpace}
        title="Remove Leased Area"
        message={(() => {
          const space = spaces.find((s) => s.id === removingSpace);
          const label = space?.name.trim() || "this area";
          return `Remove ${label} from this lease? Its unit assignment, rent and any renewals recorded against it go with it. Nothing is written until you save the tenant.`;
        })()}
        confirmLabel="Remove"
        onClose={() => setRemovingSpace(null)}
        onConfirm={() => {
          onChange({ leasedSpaces: spaces.filter((s) => s.id !== removingSpace) });
          setRemovingSpace(null);
        }}
      />

      {/* Confirmed rather than simply toggled: it is the switch that stops a
          tenant being asked for figures, and nothing else on this form would
          show that it had been thrown. */}
      <DeleteConfirmModal
        isOpen={confirmNoSales}
        title="Stop sales reporting?"
        message="Selecting No means you no longer wish this tenant to report their monthly sales. Outstanding months stop being chased, and nothing is written until you save the tenant."
        confirmLabel="Verify"
        onClose={() => setConfirmNoSales(false)}
        onConfirm={() => {
          onChange({ salesReported: false });
          setConfirmNoSales(false);
        }}
      />

      <DeleteConfirmModal
        isOpen={removingLease}
        title="Remove Occupancy"
        message={`Remove this lease? Its ${spaces.length} leased area${
          spaces.length === 1 ? "" : "s"
        } go with it. Nothing is written until you save the tenant.`}
        confirmLabel="Remove"
        onClose={() => setRemovingLease(false)}
        onConfirm={() => {
          setRemovingLease(false);
          onRemove();
        }}
      />
    </div>
  );
}
